import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { CustomerInfo } from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import { buildHomeMailingAddressPersonalDetailItems } from 'features/personalDetails/utils/ChangeAddressScreen.util';
import { TFunction } from 'i18next';
import { RefObject } from 'react';
import { Size } from 'shared/components/vxl/avatar/UserAvatar/UserAvatar.helpers';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import { isAndroid, isWeb } from 'shared/constants/platform';
import { SectionedContentScreenSection } from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { VXLComponentId, VXLGroupId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { formatDefaultPhoneNumber } from 'shared/utils/string/phone';

type AdvanceTranslatedText = TFunction<'profile', 'personalDetails'>;

export interface PersonalDetailsItemDetails {
  label: string;
  chevronVisible?: boolean;
  onPress?: () => void;
  trailingAccessory?: ListItemTrailingAccessoryProps;
  analyticsId?: string;
}

export interface PersonalDetailsItems {
  name: PersonalDetailsItemDetails;
  homeAddress?: PersonalDetailsItemDetails;
  homeAndMailingAddress?: PersonalDetailsItemDetails;
  mailingAddress?: PersonalDetailsItemDetails;
  phoneNumber: PersonalDetailsItemDetails;
  emailAddress: PersonalDetailsItemDetails;
}

interface BottomSheetProps {
  ref: RefObject<BottomSheetModal | null>;
  show: () => void;
}

const sectionListHelper = (
  personalInfo: CustomerInfo | null,
  translatedText: AdvanceTranslatedText,
  handleFaceIdToggle: (value: boolean) => void,
  faceIdState: boolean,
  bottomSheet: BottomSheetProps,
  showChangePhoneNumber: boolean,
  isHomeAndMailingAddressFeatureEnabled: boolean,
) => {
  const sections: SectionedContentScreenSection[] = [];
  const sectionDetailsList: VXLNode[] = [];
  const sectionSecurityList: VXLNode[] = [];
  const photoUrl = personalInfo?.profilePhoto?.url;
  const avatarSource = photoUrl
    ? {
        userAvatarVariant: {
          url: photoUrl,
        },
      }
    : undefined;

  if (personalInfo) {
    const homeMailingAddressItems = buildHomeMailingAddressPersonalDetailItems(
      personalInfo,
      isHomeAndMailingAddressFeatureEnabled,
    );

    const requiredPersonalDetails: PersonalDetailsItems = {
      name: {
        label: personalInfo?.name?.full ?? '',
      },
      // Possible tiles are homeAddress and maybe mailingAddress, or just homeAndMailingAddress
      ...homeMailingAddressItems,
      phoneNumber: {
        label: formatDefaultPhoneNumber(personalInfo?.phone ?? ''),
        analyticsId: showChangePhoneNumber ? 'change-phone-clicked' : '',
        trailingAccessory: showChangePhoneNumber
          ? {
              id: 1,
              name: VXLComponentId.ICON,
              props: {
                iconKey: 'EDIT',
              },
            }
          : undefined,
        onPress: showChangePhoneNumber
          ? () => router.push('/myvaro/personal-details/change-phone')
          : undefined,
      },
      emailAddress: {
        label: personalInfo?.email?.address,
        analyticsId: 'changeEmailAddressClicked',
        trailingAccessory: {
          id: 1,
          name: VXLComponentId.ICON,
          props: {
            iconKey: 'EDIT',
          },
        },
        onPress: () => router.push('/myvaro/personal-details/change-email'),
      },
    };

    Object.entries(requiredPersonalDetails).forEach(
      (fields: [string, PersonalDetailsItemDetails]) => {
        const field = fields?.[1];

        sectionDetailsList.push({
          id: field?.label,
          name: VXLComponentId.LIST_ITEM,
          props: {
            primaryCopy: field?.label,
            secondaryCopy: translatedText(`labels.${fields?.[0]}`),
            showsChevron: field?.chevronVisible ?? false,
            trailingAccessory: field?.trailingAccessory,
            lineFormat: ListItemLineFormat.LABEL,
            analyticsId: field?.analyticsId,
            onPress:
              field?.onPress !== undefined ? () => field?.onPress?.() : null,
          },
        });
      },
    );

    sections.push({
      sectionHeader: {
        id: 'personal-details-section-group',
        name: VXLGroupId.COMPONENT_GROUP,
        props: {
          alignItems: 'center',
          gap: theme.spacing.s,
          components: [
            {
              id: 'personal-details-user-avatar',
              name: VXLComponentId.USER_AVATAR,
              props: {
                userAvatarShowVaroLogo: true,
                userAvatarSize: Size.Xlarge,
                id: 'user-avatar',
                ...avatarSource,
              },
            },
            {
              id: 'personal-details-upload-photo-button',
              name: VXLComponentId.BUTTON,
              props: {
                type: 'TEXT',
                label: translatedText('buttons.uploadPhoto'),
                onPress: () => bottomSheet.show(),
                analyticsId: 'uploadPhotoClicked',
              },
            },
          ],
        },
      },
      data: sectionDetailsList,
    });

    sectionSecurityList.push({
      id: translatedText('labels.changePassword'),
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: translatedText('labels.changePassword'),
        showsChevron: true,
        lineFormat: ListItemLineFormat.LABEL,
        onPress: () => {
          router.push('/myvaro/personal-details/change-password');
        },
        analyticsId: 'changePasswordClicked',
      },
    });

    if (!isWeb) {
      const authMethod = isAndroid
        ? translatedText(`labels.biometrics`)
        : translatedText(`labels.faceId`);
      const biometric_type = isAndroid ? 'biometric' : 'face_id';
      sectionSecurityList.push({
        id: authMethod,
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: authMethod,
          showsChevron: false,
          lineFormat: ListItemLineFormat.LABEL,
          trailingAccessory: {
            id: 'switch-face-id',
            name: VXLComponentId.SWITCH,
            props: {
              onValueChange: () => {
                handleFaceIdToggle(!faceIdState);
              },
              analyticsId: faceIdState ? 'faceIdDisabled' : 'faceIdEnabled',
              customAttributes: {
                biometric_type: biometric_type,
              },
              isOn: faceIdState,
            },
          },
        },
      });
    }

    sections.push({
      sectionHeader: {
        id: 'personal-details-security-section-group',
        name: VXLGroupId.COMPONENT_GROUP,
        props: {
          components: [
            {
              id: 'personal-details-security-section-label',
              name: VXLComponentId.SECTION_DIVIDER,
              props: { labelText: translatedText('labels.security') },
            },
          ],
        },
      },
      data: sectionSecurityList,
    });
  }

  return { sections };
};

export default sectionListHelper;
